import { useCallback, useEffect, useRef, useState } from 'react';

// How long "Saved" stays under a question.
const SAVED_STATUS_MS = 3000;

const clearTimer = (timers, id) => {
    clearTimeout(timers[id]);
    delete timers[id];
};

/**
 * Saves edits made on an assessment's Review & Submit step as they happen (AX-205).
 *
 * The review step used to change answers in memory only and persist them on Submit, so a reload
 * brought back the previously saved answer. Call `requestSave(questionId)` from the change handler;
 * the save runs in an effect after the render that holds the new answer, so `save` (the flow's
 * per-question save, passed fresh each render) reads current state rather than the pre-click
 * closure. Saves run one at a time in request order, so two quick edits to the same question can't
 * land on the server out of order. A question re-requested while its save is in flight is queued
 * again and saved once more with the newer answer.
 *
 * `save(questionId)` must throw on failure and leave the question dirty, so Submit still sends it.
 * `isVisible(questionId)` drops a queued save for a follow-up that a later parent answer hid (its
 * answer is already cleared; saving it would post an empty answer for a hidden question).
 *
 * Returns `{ requestSave, saveStatus, saveErrors, whenIdle, runWhenIdle }`. `saveStatus[questionId]` is
 * 'saving' | 'saved' | 'error' ('saved' clears itself after a few seconds; an error stays until the
 * question saves); `saveErrors[questionId]` is the error a failed save threw.
 * Submit goes through `runWhenIdle(submit)`, so a review save can't land behind the submission
 * (it re-saves progress, which sets the assessment back to in progress in some flows); so does Back
 * to Questions, so a questions-step save can't race a review save of the same question. Submit is
 * not disabled while saving instead: a note's blur fires on the Submit button's mousedown, and a
 * button disabled before mouseup swallows the click.
 */
export function useReviewAutoSave(save, isVisible) {
    const saveRef = useRef(save);
    saveRef.current = save;
    const isVisibleRef = useRef(isVisible);
    isVisibleRef.current = isVisible;

    const [queue, setQueue] = useState([]);
    const queueRef = useRef(queue);
    queueRef.current = queue;
    const [saveStatus, setSaveStatus] = useState({});
    const [saveErrors, setSaveErrors] = useState({});
    const inFlightRef = useRef(false);
    const idleWaitersRef = useRef([]);
    const mountedRef = useRef(true);
    const savedTimersRef = useRef({});

    // Set in the body too: StrictMode's dev mount-unmount-mount would otherwise leave it false.
    // Pending runWhenIdle waiters are left unresolved on unmount: the page is gone, so not
    // submitting is correct.
    useEffect(() => {
        mountedRef.current = true;
        const timers = savedTimersRef.current;
        return () => {
            mountedRef.current = false;
            Object.keys(timers).forEach(id => clearTimer(timers, id));
        };
    }, []);

    const requestSave = useCallback((questionId) => {
        const id = String(questionId);
        clearTimer(savedTimersRef.current, id);
        setQueue(prev => (prev.includes(id) ? prev : [...prev, id]));
        setSaveStatus(prev => ({ ...prev, [id]: 'saving' }));
    }, []);

    useEffect(() => {
        if (inFlightRef.current) return;
        if (queue.length === 0) {
            const waiters = idleWaitersRef.current;
            idleWaitersRef.current = [];
            waiters.forEach(resolve => resolve());
            return;
        }
        const [id] = queue;
        // Dequeue before saving: an edit made while this save is in flight queues the id again.
        setQueue(prev => prev.slice(1));
        if (isVisibleRef.current && !isVisibleRef.current(id)) {
            const omitId = (prev) => {
                if (!(id in prev)) return prev;
                const next = { ...prev };
                delete next[id];
                return next;
            };
            setSaveStatus(omitId);
            setSaveErrors(omitId);
            return;
        }
        inFlightRef.current = true;
        (async () => {
            let error = null;
            try {
                await saveRef.current(id);
            } catch (err) {
                error = err || new Error('Save failed');
            }
            inFlightRef.current = false;
            if (!mountedRef.current) return;
            setSaveErrors(prev => {
                const next = { ...prev };
                if (error) next[id] = error; else delete next[id];
                return next;
            });
            setSaveStatus(prev => {
                // A newer edit queued during this save keeps the question showing 'saving'.
                if (prev[id] === 'saving' && queueRef.current.includes(id)) return prev;
                return { ...prev, [id]: error ? 'error' : 'saved' };
            });
            if (!error) {
                // Clears only a 'saved' still showing: a newer save or an error replaces it.
                clearTimer(savedTimersRef.current, id);
                savedTimersRef.current[id] = setTimeout(() => {
                    delete savedTimersRef.current[id];
                    setSaveStatus(prev => {
                        if (prev[id] !== 'saved') return prev;
                        const next = { ...prev };
                        delete next[id];
                        return next;
                    });
                }, SAVED_STATUS_MS);
            }
            // Re-run the effect for the next queued id (or to release idle waiters).
            setQueue(prev => [...prev]);
        })();
    }, [queue]);

    // Resolves once nothing is queued or in flight. Reads refs, so a click handler can call it
    // right after the blur that queued a save.
    const whenIdle = useCallback(() => {
        if (!inFlightRef.current && queueRef.current.length === 0) return Promise.resolve();
        return new Promise(resolve => idleWaitersRef.current.push(resolve));
    }, []);

    // Runs `fn` once pending saves finish (Submit and Back to Questions use this). A second call
    // while one is waiting or running is ignored, so a double-click can't submit twice and a Back
    // click can't land in the middle of a submission.
    const waitingRunRef = useRef(false);
    const runWhenIdle = useCallback(async (fn) => {
        if (waitingRunRef.current) return undefined;
        waitingRunRef.current = true;
        try {
            await whenIdle();
            return await fn();
        } finally {
            waitingRunRef.current = false;
        }
    }, [whenIdle]);

    return { requestSave, saveStatus, saveErrors, whenIdle, runWhenIdle };
}
